import {
  HandshakeInit,
  HandshakeResult,
  IMessage,
  RequestTopicSubscriptionsPayload,
  TopicSubscribePayload,
  TopicSubscriptions,
  TopicUnsubscribePayload,
  UUID,
  BrokerId,
} from 'magpie-types';
import {
  WINDOW_BROKER_PROXY_LOGGER_NAME,
  getAllowedOrigins,
} from '../../config';
import { RelayError } from '../../errors';
import { Logger } from 'simple-logging-lib';
import { MessageLink } from '../../models';
import { delay, getDeferred } from '../../utils';
import { WindowBrokerInterface } from '../window-broker-interface';
import { relayOperations } from './operations';
import { checkIfHasBrokerProxy } from './proxy-probe';

export class WindowBrokerProxy implements WindowBrokerInterface {
  protected readonly logger = Logger.getLogger(WINDOW_BROKER_PROXY_LOGGER_NAME);

  static #instance: WindowBrokerProxy;
  private readonly messageChannel: MessageChannel;
  private readonly relayPort: MessagePort;
  private link: MessageLink | null = null;
  private handshakeCompleted = false;
  #initialized = false;
  #targetOrigin: string | null = null;
  #brokerId: BrokerId | null = null;

  private constructor(private readonly scope: Window) {
    // Send message channel port to the scope
    const messageChannel = new MessageChannel();
    this.messageChannel = messageChannel;
    messageChannel.port1.start();
    this.relayPort = messageChannel.port1;
  }

  static create = async (scope: Window): Promise<WindowBrokerProxy> => {
    if (!WindowBrokerProxy.#instance) {
      const instance = new WindowBrokerProxy(scope);
      try {
        await instance.init();
      } catch (error) {
        instance.logger.error(error);
        throw error;
      }
      WindowBrokerProxy.#instance = instance;
    }
    return WindowBrokerProxy.#instance;
  };

  static reset = async () => {
    if (!WindowBrokerProxy.#instance) {
      return;
    }
    await WindowBrokerProxy.#instance.reset();
    WindowBrokerProxy.#instance = undefined as any;
  };

  private waitForCompletion = (requestId: UUID): Promise<void> => {
    const deferred = getDeferred<void>();
    const onReply = (event: MessageEvent) => {
      if (
        relayOperations.opCompletedConfirmation.match(event.data) &&
        event.data.payload === requestId
      ) {
        deferred.resolve();
        this.relayPort.removeEventListener('message', onReply);
      }
    };
    this.relayPort.addEventListener('message', onReply);
    return deferred.promise;
  };

  private waitForError = (requestId: UUID): Promise<void> => {
    const deferred = getDeferred<void>();
    const onError = (event: MessageEvent) => {
      if (
        relayOperations.opFailedConfirmation.match(event.data) &&
        event.data.payload.replyTo === requestId
      ) {
        deferred.reject(event.data.payload.error);
        this.relayPort.removeEventListener('message', onError);
      }
    };
    this.relayPort.addEventListener('message', onError);
    return deferred.promise;
  };

  private waitForOperationResult = (requestId: UUID): Promise<void> => {
    const timeout = delay(5000);
    const onSuccess = () => {
      timeout.reset();
    };
    return Promise.race([
      this.waitForCompletion(requestId).then(onSuccess),
      this.waitForError(requestId).then(onSuccess),
      timeout.then(() => {
        throw new RelayError(RelayError.Reason.OPERATION_TIMEOUT);
      }),
    ]);
  };

  private init = async () => {
    const result = await checkIfHasBrokerProxy(this.scope);
    if (!result?.origin) {
      throw new RelayError(RelayError.Reason.ORIGIN_NOT_RETRIEVED);
    }
    if (!getAllowedOrigins().includes(result.origin)) {
      throw new RelayError(RelayError.Reason.ORIGIN_NOT_ALLOWED);
    }
    this.#targetOrigin = result.origin;
    this.#brokerId = result.brokerId;
    this.#initialized = true;
  };

  handshake = (
    payload: HandshakeInit,
    link: MessageLink
  ): Promise<HandshakeResult> => {
    if (!this.#initialized) {
      throw new RelayError(RelayError.Reason.PROXY_NOT_INITIALIZED);
    }
    if (!this.#targetOrigin) {
      throw new RelayError(RelayError.Reason.ORIGIN_NOT_RETRIEVED);
    }
    const message = relayOperations.handshake(payload);
    const deferred = getDeferred<HandshakeResult>();
    this.link = link;

    const onReply = (event: MessageEvent) => {
      if (
        !relayOperations.handshakeResponse.match(event.data) ||
        event.data.payload.replyTo !== message.id
      ) {
        return;
      }
      if (event.origin !== this.#targetOrigin) {
        this.logger.debug('Ignoring message from invalid origin', event.origin);
      }

      this.logger.debug('Proxy handshake completed', event.data.payload);
      deferred.resolve(event.data.payload);
      this.relayPort.removeEventListener('message', onReply);
      this.handshakeCompleted = true;
      this.#brokerId = event.data.payload.brokerId;
    };

    this.relayPort.addEventListener('message', onReply);
    this.logger.debug('Sending proxy handshake message', message);
    const transfer = [this.messageChannel.port2];
    this.scope.postMessage(message, this.#targetOrigin, transfer);
    this.relayPort.addEventListener('message', this.onRelayMessage);
    return deferred.promise;
  };

  onRelayMessage = (event: MessageEvent): void => {
    if (!relayOperations.relayMessage.match(event.data)) {
      return;
    }
    if (event.origin !== this.#targetOrigin) {
      this.logger.debug(
        'Ignoring relay message from invalid origin',
        event.origin
      );
    }
    if (!this.link) {
      this.logger.error(new RelayError(RelayError.Reason.NO_LINK_SET));
      return;
    }
    this.link.next(event.data.payload);
  };

  topicSubscribe = (payload: TopicSubscribePayload): Promise<void> => {
    if (!this.handshakeCompleted) {
      throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
    }
    const message = relayOperations.topicSubscribe(payload);
    const completionPromise = this.waitForOperationResult(message.id);
    this.relayPort.postMessage(message);
    return completionPromise;
  };

  topicUnsubscribe = (payload: TopicUnsubscribePayload): Promise<void> => {
    if (!this.handshakeCompleted) {
      throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
    }
    const message = relayOperations.topicUnsubscribe(payload);
    const completionPromise = this.waitForOperationResult(message.id);
    this.relayPort.postMessage(message);
    return completionPromise;
  };

  getTopicSubscriptions = (
    payload: RequestTopicSubscriptionsPayload
  ): Promise<TopicSubscriptions> => {
    if (!this.handshakeCompleted) {
      throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
    }
    const deferred = getDeferred<TopicSubscriptions>();
    const message = relayOperations.getTopicSubscriptions(payload);

    const onReply = (event: MessageEvent) => {
      if (
        relayOperations.getTopicSubscriptionsResponse.match(event.data) &&
        event.data.payload.replyTo === message.id
      ) {
        deferred.resolve(event.data.payload);
      }
    };
    this.relayPort.addEventListener('message', onReply);
    this.relayPort.postMessage(message);
    return deferred.promise;
  };

  close = (clientId: string): Promise<void> => {
    if (!this.handshakeCompleted) {
      throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
    }
    const message = relayOperations.close({ clientId });
    const completionPromise = this.waitForOperationResult(message.id);
    this.relayPort.postMessage(message);
    return completionPromise;
  };

  reset = (): Promise<void> => {
    this.link?.complete();
    this.relayPort.close();
    this.handshakeCompleted = false;
    this.messageChannel.port2.close();
    // @ts-expect-error - Resetting the singleton instance for testing purposes
    WindowBrokerProxy.#instance = undefined;
    return Promise.resolve();
  };

  sendMessage = (payload: IMessage): Promise<void> => {
    if (!this.handshakeCompleted) {
      throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
    }
    const message = relayOperations.sendMessage(payload);
    const completionPromise = this.waitForOperationResult(message.id);
    this.relayPort.postMessage(message);
    return completionPromise;
  };

  getBrokerId = (): Readonly<BrokerId> => {
    if (!this.#initialized) {
      throw new RelayError(RelayError.Reason.PROXY_NOT_INITIALIZED);
    }
    if (!this.#brokerId) {
      throw new RelayError(RelayError.Reason.PROXY_NOT_INITIALIZED);
    }
    return this.#brokerId;
  };
}
